import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeReturnDisposalUnprocessedCountResponse } from '~/types/receive.types';

/** [입고관리 > 회송/폐기 관리] GNB 미처리 건수(신청대기+회송 정보입력대기) 조회 API */
const fetchReturnDisposalUnprocessedCount = async (): Promise<number> => {
  const { data } = await axiosInstance.get<TypeReturnDisposalUnprocessedCountResponse>(
    API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_UNPROCESSED_COUNT
  );

  return data.data.count;
};

type TypeUseGetReturnDisposalUnprocessedCountQueryParams = {
  enabled?: boolean;
};

/**
 * [입고관리 > 회송/폐기 관리] GNB 미처리 건수 조회 Query Hook
 *
 * 주기적 폴링 없이 아래 3시점에만 (재)호출한다.
 * 1. 포털 최초 로드(로그인/새로고침): 마운트 시 조회 + `staleTime: 0`
 * 2. 상태 변경 액션(클레임/회수/폐기 신청) 완료 직후: 사용처에서 해당 쿼리키 무효화
 * 3. 브라우저 탭/창 재포커스: `refetchOnWindowFocus`
 *
 * 회송/폐기 접근 권한이 있을 때만 조회한다(`enabled`). 백그라운드 재호출이므로
 * 에러 토스트를 억제하기 위해 `meta.showGlobalErrorToast: false`를 지정한다.
 */
export const useGetReturnDisposalUnprocessedCountQuery = ({
  enabled = true,
}: TypeUseGetReturnDisposalUnprocessedCountQueryParams = {}) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalUnprocessedCount(),
    queryFn: fetchReturnDisposalUnprocessedCount,
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    meta: { showGlobalErrorToast: false },
  });
};

/** [입고관리 > 회송/폐기 관리] GNB 미처리 건수 조회 Mock (시나리오 고정 응답) */
registerMockAPI<TypeReturnDisposalUnprocessedCountResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_UNPROCESSED_COUNT,
  label: '회송폐기관리/미처리 건수 조회',
  scenarios: [
    {
      name: '정상 응답 (2건)',
      response: { data: { count: 2 } },
    },
    {
      name: '미처리 없음 (0건)',
      response: { data: { count: 0 } },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '미처리 건수를 불러오지 못했습니다.' } },
      status: 500,
    },
    {
      name: '토큰 만료 (401)',
      response: { error: { message: '토큰이 만료되었습니다.' } },
      status: 401,
    },
  ],
});
